import React from "react";
import { Navigate, useLocation, Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import Reveal from "../components/site/Reveal";
import WaitlistForm from "../components/site/WaitlistForm";
import { PROMO } from "../lib/promo";
import { PROMO_IDS } from "../constants/testIds";
import { trackEvent } from "../lib/track";
import { BOOKING_URL, IMAGES, CONTACT } from "../lib/site";
import { useSeo, SITE } from "../lib/seo";

const EASE = [0.22, 1, 0.36, 1];

const containerStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
};

const lineMask = {
  hidden: { y: "110%" },
  visible: { y: "0%", transition: { duration: 0.9, ease: EASE } },
};

const softRise = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 1.0, ease: EASE } },
};

const offerSchema = () => ({
  "@context": "https://schema.org",
  "@type": "ItemList",
  name: "Alae Dental Grand Opening Offers",
  itemListElement: [
    {
      "@type": "ListItem",
      position: 1,
      item: {
        "@type": "Offer",
        name: PROMO.newPatient.title,
        description: `${PROMO.newPatient.giftLine} Includes: ${PROMO.newPatient.includes.join(
          ", "
        )}.`,
        availability: "https://schema.org/LimitedAvailability",
        url: `${SITE.url}${PROMO.newPatientPath}`,
        seller: { "@id": `${SITE.url}/#dentist` },
        itemOffered: { "@type": "Service", name: PROMO.newPatient.title },
      },
    },
    ...PROMO.offers.map((o, i) => ({
      "@type": "ListItem",
      position: i + 2,
      item: {
        "@type": "Offer",
        name: o.name,
        description: o.description,
        price: o.price.replace(/[^0-9.]/g, ""),
        priceCurrency: "CAD",
        availability: "https://schema.org/LimitedAvailability",
        url: `${SITE.url}${PROMO.path}`,
        seller: { "@id": `${SITE.url}/#dentist` },
        itemOffered: { "@type": "Service", name: o.name },
      },
    })),
  ],
});

export default function GrandOpening() {
  const reduce = useReducedMotion();
  const { search } = useLocation();
  const sourceOffer = new URLSearchParams(search).get("offer");
  const { scrollYProgress } = useScroll();
  const heroImgY = useTransform(scrollYProgress, [0, 0.25], ["0%", "10%"]);
  const heroImgScale = useTransform(scrollYProgress, [0, 0.25], [1.02, 1.06]);

  useSeo({
    title:
      "Grand Opening Offers. Invisalign $4,500 and Zoom! Whitening $399 | Alae Dental",
    description:
      "Alae Dental limited time offers in Vaughan and Woodbridge. Comprehensive Invisalign treatment for $4,500 including retainers and take-home whitening, Philips Zoom! in-office whitening for $399, and a Philips Sonicare welcome gift with the New Patient Experience.",
    path: PROMO.path,
    jsonLd: offerSchema(),
    jsonLdId: "grand-opening-jsonld",
  });

  if (!PROMO.enabled) return <Navigate to="/" replace />;

  return (
    <main data-testid={PROMO_IDS.page} className="bg-linen text-umber">
      {/* Kinetic Hero */}
      <section className="relative overflow-hidden paper-grain min-h-[62svh] flex items-end pt-24 md:pt-32 pb-16 md:pb-24 bg-paper">
        <motion.div
          className="absolute inset-0 opacity-40"
          style={reduce ? undefined : { y: heroImgY, scale: heroImgScale }}
          initial={reduce ? false : { scale: 1.1, opacity: 0 }}
          animate={reduce ? undefined : { scale: 1.02, opacity: 0.4 }}
          transition={{ duration: 1.6, ease: EASE }}
        >
          <img
            src={IMAGES.experience}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            decoding="sync"
            className="absolute inset-0 w-full h-[115%] object-cover"
            style={{ filter: "saturate(0.85) contrast(0.95)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-paper/60 via-paper/30 to-paper/80" />
        </motion.div>

        <motion.div
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 w-full"
          variants={containerStagger}
          initial="hidden"
          animate="visible"
        >
          <div className="flex items-center gap-4 mb-6">
            <motion.div
              className="h-[1px] w-10 md:w-14 bg-forest origin-left"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.1, ease: EASE, delay: 0.1 }}
            />
            <motion.div variants={softRise} className="font-meta text-forest">
              {PROMO.eyebrow}
            </motion.div>
          </div>

          <h1 className="font-display text-5xl md:text-6xl lg:text-[80px] leading-[1.12] md:leading-[1.06] tracking-tight text-umber max-w-4xl">
            <span className="block overflow-hidden">
              <motion.span variants={lineMask} className="block">
                Three ways to
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span variants={lineMask} className="block">
                <span className="font-display-italic text-forest">
                  welcome you.
                </span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            variants={softRise}
            className="mt-8 text-base md:text-lg leading-relaxed text-umber/85 max-w-2xl"
          >
            {PROMO.intro}
          </motion.p>

          <motion.div variants={softRise} className="mt-9 flex flex-col sm:flex-row gap-4">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={PROMO_IDS.pageBook}
              onClick={() =>
                trackEvent("book_click", {
                  offer: sourceOffer || null,
                  placement: "grand-opening-hero",
                })
              }
              className="bg-forest text-paper rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-sage transition-colors duration-300 text-center min-h-[44px]"
            >
              Book Online
            </a>
            <a
              href="#offer-enquiry"
              data-testid={PROMO_IDS.pageAsk}
              className="border border-umber/40 text-umber rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:border-forest hover:text-forest transition-colors duration-300 text-center min-h-[44px]"
            >
              Ask about this offer
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* Offers */}
      <section className="bg-linen paper-grain py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10 items-stretch">
            <Reveal className="h-full">
              <article
                data-testid={PROMO_IDS.newPatientBlock}
                className="h-full flex flex-col bg-paper border border-forest/25 shadow-paper p-8 md:p-10"
              >
                <div className="font-meta text-forest">
                  {PROMO.newPatient.eyebrow}
                </div>

                <div className="mt-7 border-l border-forest/30 pl-5">
                  <div className="font-meta text-umber/55">
                    {PROMO.newPatient.giftLabel}
                  </div>
                  <p className="mt-2.5 font-display text-xl md:text-2xl leading-snug text-forest">
                    {PROMO.newPatient.giftLine}
                  </p>
                </div>

                <h2 className="mt-7 font-display text-xl md:text-2xl leading-snug text-umber">
                  {PROMO.newPatient.title}
                </h2>

                <ul className="mt-6 border-t border-umber/15">
                  {PROMO.newPatient.includes.map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-3 py-3 border-b border-umber/15"
                    >
                      <Check
                        size={15}
                        strokeWidth={2}
                        className="mt-1 shrink-0 text-forest"
                      />
                      <span className="text-sm md:text-base leading-relaxed text-umber/85">
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-7">
                  <p className="text-xs leading-relaxed text-umber/60">
                    {PROMO.newPatient.finePrint}
                  </p>
                  <div className="mt-6 flex flex-col sm:flex-row lg:flex-col gap-3">
                    <Link
                      to={`${PROMO.newPatientPath}?from=grand-opening`}
                      data-testid={PROMO_IDS.newPatientCta}
                      className="bg-forest text-paper rounded-full px-7 py-3.5 text-[11px] tracking-[0.22em] uppercase hover:bg-sage transition-colors duration-300 text-center min-h-[44px] inline-flex items-center justify-center"
                    >
                      See the details
                    </Link>
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={PROMO_IDS.newPatientBook}
                      onClick={() =>
                        trackEvent("book_click", {
                          offer: PROMO.newPatient.id,
                          placement: "grand-opening-new-patient",
                        })
                      }
                      className="group border border-umber/35 text-umber rounded-full px-7 py-3.5 text-[11px] tracking-[0.22em] uppercase hover:border-forest hover:text-forest transition-colors duration-300 text-center min-h-[44px] inline-flex items-center justify-center gap-2"
                    >
                      Book Online
                      <ArrowUpRight
                        size={14}
                        className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      />
                    </a>
                  </div>
                </div>
              </article>
            </Reveal>

            {PROMO.offers.map((o, i) => (
              <Reveal key={o.id} delay={(i + 1) * 120} className="h-full">
                <article
                  data-testid={PROMO_IDS.pageOffer(i)}
                  className="h-full flex flex-col bg-paper border border-umber/15 shadow-paper p-8 md:p-10"
                >
                  <div className="font-meta text-forest">{PROMO.eyebrow}</div>

                  <div className="mt-7 font-display text-4xl md:text-5xl leading-none text-umber">
                    {o.price}
                  </div>

                  <h2 className="mt-5 font-display text-xl md:text-2xl leading-snug text-umber">
                    {o.name}
                  </h2>
                  <p className="mt-3 text-sm md:text-base leading-relaxed text-umber/85">
                    {o.description}
                  </p>
                  <p className="mt-4 text-sm leading-relaxed text-umber/70">
                    {o.detail}
                  </p>

                  <div className="mt-auto pt-8">
                    <div className="pt-5 border-t border-umber/15 text-[11px] tracking-[0.16em] uppercase text-umber/55">
                      {o.provider}
                    </div>
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={PROMO_IDS.pageOfferBook(i)}
                      onClick={() =>
                        trackEvent("book_click", {
                          offer: o.id,
                          placement: "grand-opening-card",
                        })
                      }
                      className="group inline-flex items-center gap-3 mt-6 text-[12px] tracking-[0.22em] uppercase text-forest py-3 min-h-[44px]"
                    >
                      Book this treatment
                      <ArrowUpRight
                        size={16}
                        className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                      />
                    </a>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={200}>
            <p
              data-testid={PROMO_IDS.finePrint}
              className="mt-14 text-xs leading-relaxed text-umber/60 max-w-2xl"
            >
              {PROMO.finePrint} Questions about whether an offer suits you? Call
              us at{" "}
              <a
                href={`tel:${CONTACT.phoneTel}`}
                className="underline underline-offset-4 hover:text-forest transition-colors"
              >
                {CONTACT.phoneLabel}
              </a>
              .
            </p>
          </Reveal>
        </div>
      </section>

      {/* Enquiry */}
      <section
        id="offer-enquiry"
        className="bg-paper paper-grain py-24 md:py-36 scroll-mt-24"
      >
        <div className="max-w-5xl mx-auto px-6 md:px-12 lg:px-16">
          <Reveal>
            <div className="font-meta text-forest mb-6">Ask About This Offer</div>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.08] tracking-tight text-umber max-w-2xl">
              Tell us what you're{" "}
              <span className="font-display-italic">considering.</span>
            </h2>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-7 text-base md:text-lg leading-relaxed text-umber/85 max-w-2xl">
              Leave your details and we'll reach out within 24 hours to confirm
              the offer, answer your questions, and find a time that works.
            </p>
          </Reveal>
          <Reveal delay={250}>
            <div className="mt-12 bg-linen/70 border border-umber/15 p-8 md:p-12 shadow-paper">
              <WaitlistForm />
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
